import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Phone, User, MessageSquare, Send, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    property_type: 'residencial',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        property_type: 'residencial',
        message: '',
      });
      
      // Reset success message after 5 seconds
      setTimeout(() => setSubmitted(false), 5000);
    } catch (error) {
      console.error('Error al enviar formulario:', error);
      alert('Error al enviar el formulario. Por favor intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <section id="contact" className="py-16 sm:py-24 md:py-32 bg-gradient-to-b from-zinc-50 to-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16">
          {/* Left: Info */}
          <div>
            <span className="text-sm font-medium uppercase tracking-widest text-zinc-500 mb-4 block">
              Contactanos
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-zinc-900 mb-4 sm:mb-6">
              Comienza tu Transicion
              <br />
              a la Energia Solar
            </h2>
            <p className="text-base sm:text-lg text-zinc-600 leading-relaxed mb-8 sm:mb-12">
              Nuestro equipo de expertos está listo para ayudarte. Solicita una consulta gratuita 
              y descubre cómo la energía solar puede transformar tu hogar o negocio.
            </p>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="bg-[#90EE90]/10 p-3 border border-[#90EE90]/30">
                  <MessageSquare className="text-[#90EE90]" size={24} />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">WhatsApp</p>
                  <a href="https://wa.me/595991344956" target="_blank" rel="noopener noreferrer" className="text-lg font-medium text-zinc-900 hover:text-[#14532D] transition-colors">+595 991 344956</a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="bg-[#90EE90]/10 p-3 border border-[#90EE90]/30">
                  <Mail className="text-[#90EE90]" size={24} />
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Email</p>
                  <a href="mailto:zenexparaguay@gmail.com" className="text-lg font-medium text-zinc-900 hover:text-[#14532D] transition-colors">zenexparaguay@gmail.com</a>
                </div>
              </div>
            </div>

            {/* Benefits */}
            <div className="mt-12 pt-8 border-t border-zinc-200">
              <h3 className="text-lg font-semibold text-zinc-900 mb-4">Qué incluye tu consulta:</h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-2 text-zinc-600">
                  <div className="w-1.5 h-1.5 bg-[#90EE90] rounded-full"></div>
                  Análisis personalizado de tu consumo
                </li>
                <li className="flex items-center gap-2 text-zinc-600">
                  <div className="w-1.5 h-1.5 bg-[#90EE90] rounded-full"></div>
                  Propuesta técnica y económica
                </li>
                <li className="flex items-center gap-2 text-zinc-600">
                  <div className="w-1.5 h-1.5 bg-[#90EE90] rounded-full"></div>
                  Simulación de ahorro y ROI
                </li>
                <li className="flex items-center gap-2 text-zinc-600">
                  <div className="w-1.5 h-1.5 bg-[#90EE90] rounded-full"></div>
                  Opciones de financiamiento
                </li>
              </ul>
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-white border-2 border-zinc-100 p-6 sm:p-8 md:p-12 shadow-lg">
            {submitted ? (
              <div className="text-center py-12" data-testid="success-message">
                <CheckCircle className="text-[#90EE90] mx-auto mb-4" size={64} />
                <h3 className="text-2xl font-semibold text-zinc-900 mb-3">¡Mensaje Enviado!</h3>
                <p className="text-zinc-600">
                  Gracias por tu interés. Nuestro equipo se pondrá en contacto contigo a la brevedad.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <h3 className="text-2xl font-semibold text-zinc-900 mb-6">Solicitar Consulta Gratuita</h3>

                {/* Name */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Nombre Completo *
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 transform -translate-y-1/2 text-zinc-400" size={20} />
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Juan Pérez"
                      className="w-full pl-12 pr-4 py-3 border-2 border-zinc-200 focus:border-[#90EE90] focus:outline-none transition-colors"
                      data-testid="contact-name-input"
                    />
                  </div>
                </div>

                {/* Email */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Email *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-zinc-400" size={20} />
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="juan@ejemplo.com"
                      className="w-full pl-12 pr-4 py-3 border-2 border-zinc-200 focus:border-[#90EE90] focus:outline-none transition-colors"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                {/* Phone */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Teléfono (Opcional)
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 text-zinc-400" size={20} />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+595 XXX XXX XXX"
                      className="w-full pl-12 pr-4 py-3 border-2 border-zinc-200 focus:border-[#90EE90] focus:outline-none transition-colors"
                      data-testid="contact-phone-input"
                    />
                  </div>
                </div>

                {/* Property Type */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Tipo de Propiedad
                  </label>
                  <select
                    name="property_type"
                    value={formData.property_type}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-zinc-200 focus:border-[#90EE90] focus:outline-none transition-colors bg-white"
                    data-testid="contact-property-type-select"
                  >
                    <option value="residencial">Residencial</option>
                    <option value="comercial">Comercial</option>
                    <option value="industrial">Industrial</option>
                  </select>
                </div>

                {/* Message */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Mensaje *
                  </label>
                  <textarea
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Cuéntanos sobre tu proyecto..."
                    rows={4}
                    className="w-full px-4 py-3 border-2 border-zinc-200 focus:border-[#90EE90] focus:outline-none transition-colors resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#90EE90] text-[#14532D] hover:bg-[#7CDF7C] px-8 py-4 font-bold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="contact-submit-button"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#14532D] border-t-transparent"></div>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      Enviar Consulta
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;